import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  getAllCourseListByTeacher,
  resetCourseList,
} from "../../state/actions/getAllCourseListByTeacherAction";
import {
  getAllCourseListByStudent,
  resetCourseListStudent,
} from "../../state/actions/getAllCourseListByStudentAction";
import { useDispatch, useSelector } from "react-redux";
import { useAuth } from "../../features/auth/AuthContext";
import { getOverallPerformanceScoreByStudent } from "../../state/actions/getOverallPerformanceScoreByStudentAction";
import { NoCoursesFoundImage, unarchive_btn } from "../../assets/images/images";
import {
  setSelectedCourseData,
  setSelectedStudentData,
  setAvailableStudents,
} from "../../state/reducers/selectedCourseDataReducer";
import CourseListLoader from "./CourseListLoader";
import { Tooltip } from "react-tooltip";

const CoursesSelectFilter = ({
  selectedCourseData,
  setselectedCourseData,
  periodTab,
  year,
  month,
  start_date,
  end_date,
  month_year,
  selectstudent,
  setselectstudent,
  value,
  isAIGenerating,
}) => {
  const getMonthName = (monthNumber) => {
    const months = [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
    ];
    const validMonth = parseInt(monthNumber, 10);
    if (isNaN(validMonth) || validMonth < 1 || validMonth > 12) return "Jan";
    return months[validMonth - 1];
  };

  const dispatch = useDispatch();
  const { token } = useAuth();

  const prevActiveCoursesRef = useRef([]);
  const hasUserInteractedRef = useRef(false);
  const isApiCallInProgressRef = useRef(false);
  const prevTabRef = useRef("my_courses");

  const globalState = useSelector((state) => state.globalState);
  const teacher_id = globalState?.teacher_details?.data?.teacher_id;
  const role = globalState?.role;
  const student_id = globalState?.student_details?.data?.student_id;

  const saveCourseSelections = (selections) => {
    try {
      const storageKey =
        role === "Teacher"
          ? `teacher_courses_${teacher_id}`
          : `student_courses_${student_id}`;
      localStorage.setItem(storageKey, JSON.stringify(selections));
    } catch (error) {}
  };

  const clearCourseSelections = () => {
    try {
      const storageKey =
        role === "Teacher"
          ? `teacher_courses_${teacher_id}`
          : `student_courses_${student_id}`;
      localStorage.removeItem(storageKey);
    } catch (error) {}
  };

  const extractStudentsFromCourses = (coursesData) => {
    const studentMap = new Map();
    coursesData.forEach((course) => {
      if (course.students && Array.isArray(course.students)) {
        course.students.forEach((student) => {
          if (!studentMap.has(student.student_id)) {
            studentMap.set(student.student_id, {
              student_id: student.student_id,
              student_name: student.student_name,
              img_url: student.img_url,
            });
          }
        });
      }
    });
    return Array.from(studentMap.values());
  };

  const [courseActiveTab, setCourseActiveTab] = useState("my_courses");

  const teacherCourseState = useSelector(
    (state) => state?.getAllCourseListByTeacher || {}
  );

  // FIX: Memoize all arrays derived from Redux state.
  //    `|| []` creates a new array reference on every render when data is
  //    undefined/null. useEffects that depend on these arrays then fire every
  //    render → setState → re-render → new array → repeat = max depth exceeded.

  const myCoursesRaw = teacherCourseState?.courses;
  const myCourses = useMemo(() => myCoursesRaw || [], [myCoursesRaw]);

  const departmentCoursesRaw = useSelector(
    (state) =>
      state?.getAllCourseListByTeacher?.department_role_data?.department_courses
  );
  const departmentCourses = useMemo(
    () => departmentCoursesRaw || [],
    [departmentCoursesRaw]
  );

  //  FIX: Memoize activeCourses — it's derived from two memoized arrays so
  //    it will only change when the underlying data actually changes.
  const activeCourses = useMemo(
    () => (courseActiveTab === "my_courses" ? myCourses : departmentCourses),
    [courseActiveTab, myCourses, departmentCourses]
  );

  const isTeacherCoursesLoading = useSelector(
    (state) => state?.getAllCourseListByTeacher?.isLoading || false
  );

  const [courseData, setcourseData] = useState([]);
  const [selectedCourses, setSelectedCourses] = useState([]);

  const studentcourse = useSelector(
    (state) => state?.getAllCourseListByStudent
  );

  //  FIX: Memoize student courses array separately so effects can depend on
  //    just the courses array rather than the whole Redux slice object.
  const studentCoursesRaw = studentcourse?.courses;
  const studentCoursesList = useMemo(
    () => studentCoursesRaw || [],
    [studentCoursesRaw]
  );

  const isStudentCoursesLoading = useSelector(
    (state) => state?.getAllCourseListByStudent?.isLoading || false
  );
  const [courseStudentData, setcourseStudentData] = useState(studentCoursesList);
  const [selectedStudentCourses, setSelectedStudentCourses] = useState([]);

  //  FIX: Depend on stable `studentCoursesList` instead of `studentcourse?.courses`
  useEffect(() => {
    setcourseStudentData(studentCoursesList);

    if (studentCoursesList.length > 0) {
      const availableCourseIds = studentCoursesList.map((c) => c.course_id);
      const validSelectedCourses = selectedStudentCourses.filter((id) =>
        availableCourseIds.includes(id)
      );
      if (validSelectedCourses.length !== selectedStudentCourses.length) {
        setSelectedStudentCourses(validSelectedCourses);
        setselectstudent(validSelectedCourses);
      }
    }
  }, [studentCoursesList]); // ✅ stable memoized reference

  const [searchCourse, setsearchCourse] = useState("");

  const convertDateFormat = (dateString) => {
    if (!dateString) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return dateString;
    if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
      const [day, month, year] = dateString.split("-");
      return `${year}-${month}-${day}`;
    }
    return dateString;
  };

  const getAllCourseListByTeacherRequest = useCallback(() => {
    let startDateParam = null;
    let endDateParam = null;

    if (periodTab === "custom" && start_date && end_date) {
      startDateParam = convertDateFormat(start_date);
      endDateParam = convertDateFormat(end_date);
    } else if (periodTab === "month" && month_year) {
      startDateParam = month_year;
    } else if (periodTab === "week") {
      if (month_year) {
        startDateParam = month_year;
      } else if (month && year) {
        startDateParam = `${getMonthName(parseInt(month, 10))}-${year}`;
      }
    } else if (periodTab === "year" && year) {
      startDateParam = year.toString();
    }

    if (startDateParam || periodTab === "year") {
      dispatch(
        getAllCourseListByTeacher(
          teacher_id,
          token,
          periodTab,
          startDateParam,
          endDateParam
        )
      );
    }
  }, [teacher_id, token, periodTab, start_date, end_date, month_year, year, dispatch]);

  const getAllCourseListByStudentRequest = useCallback(() => {
    let startDateParam = null;
    let endDateParam = null;

    if (periodTab === "custom" && start_date && end_date) {
      startDateParam = convertDateFormat(start_date);
      endDateParam = convertDateFormat(end_date);
    } else if (periodTab === "month" && month_year) {
      startDateParam = month_year;
    } else if (periodTab === "week") {
      if (month_year) {
        startDateParam = month_year;
      } else if (month && year) {
        startDateParam = `${getMonthName(parseInt(month, 10))}-${year}`;
      }
    } else if (periodTab === "year" && year) {
      startDateParam = year.toString();
    }

    if (startDateParam || periodTab === "year") {
      dispatch(
        getAllCourseListByStudent(
          student_id,
          token,
          periodTab,
          startDateParam,
          endDateParam
        )
      );
    }
  }, [student_id, token, periodTab, start_date, end_date, month_year, year, month, dispatch]);

  const getCurrentDateFormatted = () => {
    const today = new Date();
    return `${today.getDate().toString().padStart(2, "0")}-${(
      today.getMonth() + 1
    )
      .toString()
      .padStart(2, "0")}-${today.getFullYear()}`;
  };

  const getWeekAgoDateFormatted = () => {
    const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    return `${weekAgo.getDate().toString().padStart(2, "0")}-${(
      weekAgo.getMonth() + 1
    )
      .toString()
      .padStart(2, "0")}-${weekAgo.getFullYear()}`;
  };

  const getOverallPerformanceScoreByStudentRequest = () => {
    const coursesString = Array.isArray(selectedStudentCourses)
      ? selectedStudentCourses.join(",")
      : "";

    if (coursesString.length > 0 && student_id) {
      let monthYearValue = month_year;
      if (!monthYearValue && month && year) {
        const months = [
          "Jan", "Feb", "Mar", "Apr", "May", "Jun",
          "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
        ];
        const validMonth = parseInt(month, 10);
        if (!isNaN(validMonth) && validMonth >= 1 && validMonth <= 12) {
          monthYearValue = `${months[validMonth - 1]}-${year}`;
        }
      }

      let customStartDate = start_date;
      let customEndDate = end_date;
      if (periodTab === "custom") {
        if (!customStartDate) customStartDate = getWeekAgoDateFormatted();
        if (!customEndDate) customEndDate = getCurrentDateFormatted();
      }

      const data = {
        student_id,
        time_period: periodTab,
        courses: coursesString,
        ...((periodTab === "week" ||
          periodTab === "month" ||
          periodTab === "year") && { month, month_year: monthYearValue }),
        ...(periodTab !== "custom" && { year }),
        ...(periodTab === "custom" && {
          start_date: customStartDate,
          end_date: customEndDate,
        }),
      };

      dispatch(getOverallPerformanceScoreByStudent(token, data));
    }
  };

  useEffect(() => {
    if (student_id && studentCoursesList.length > 0) {
      const filtered = studentCoursesList.filter((c) =>
        c.course_name.toLowerCase().includes(searchCourse.toLowerCase())
      );
      setcourseStudentData(filtered);
    }
  }, [searchCourse, studentCoursesList, student_id]); // ✅ stable memoized reference

  // Filter + available students for teacher
  useEffect(() => {
    if (role !== "Teacher") return;

    const filtered = activeCourses.filter((c) =>
      c.course_name.toLowerCase().includes(searchCourse.toLowerCase())
    );
    setcourseData(filtered);

    if (!searchCourse.trim()) {
      const extractedStudents = extractStudentsFromCourses(activeCourses);
      dispatch(
        setAvailableStudents(extractedStudents.length > 0 ? extractedStudents : [])
      );
    }
  }, [searchCourse, activeCourses, role, dispatch]); // ✅ activeCourses is now stable via useMemo

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      dispatch(resetCourseList());
      dispatch(resetCourseListStudent());
    };
  }, []);

  const handleCheck = (course_id) => {
    if (isAIGenerating) return;
    hasUserInteractedRef.current = true;

    if (role === "Teacher") {
      const newSelection = selectedCourses.includes(course_id)
        ? selectedCourses.filter((id) => id !== course_id)
        : [...selectedCourses, course_id];
      setSelectedCourses(newSelection);
      setselectedCourseData(newSelection);
      dispatch(setSelectedCourseData(newSelection));
      saveCourseSelections(newSelection);
    } else {
      const newSelection = selectedStudentCourses.includes(course_id)
        ? selectedStudentCourses.filter((id) => id !== course_id)
        : [...selectedStudentCourses, course_id];
      setSelectedStudentCourses(newSelection);
      setselectstudent(newSelection);
      dispatch(setSelectedCourseData(newSelection));
      saveCourseSelections(newSelection);
    }
  };

  // Fetch course lists when filters change
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (role === "Student" && student_id) {
        getAllCourseListByStudentRequest();
      }
      if (teacher_id && token) {
        getAllCourseListByTeacherRequest();
      }
    }, 200);
    return () => clearTimeout(timeoutId);
  }, [
    teacher_id,
    token,
    student_id,
    periodTab,
    start_date,
    end_date,
    month_year,
    year,
    getAllCourseListByTeacherRequest,
    getAllCourseListByStudentRequest,
  ]);

  // Student overall performance API call
  useEffect(() => {
    if (
      role === "Student" &&
      selectedStudentCourses.length > 0 &&
      studentCoursesList.length > 0 && // ✅ stable memoized reference
      !isApiCallInProgressRef.current &&
      value === "1"
    ) {
      const timeoutId = setTimeout(() => {
        getOverallPerformanceScoreByStudentRequest();
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [
    selectedStudentCourses,
    periodTab,
    year,
    month,
    start_date,
    end_date,
    month_year,
    value,
  ]);

  // Auto-select all courses for TEACHER when courses load or tab changes
  useEffect(() => {
    if (!teacher_id || searchCourse.trim()) return;

    const prevIds = prevActiveCoursesRef.current;
    const currentIds = activeCourses.map((c) => c.course_id);

    const hasChanged =
      JSON.stringify([...currentIds].sort()) !==
      JSON.stringify([...prevIds].sort());

    const tabChanged = prevTabRef.current !== courseActiveTab;
    prevTabRef.current = courseActiveTab;

    if (!hasChanged && !tabChanged) return;

    prevActiveCoursesRef.current = currentIds;

    if (currentIds.length > 0) {
      setSelectedCourses(currentIds);
      setselectedCourseData(currentIds);
      dispatch(setSelectedCourseData(currentIds));
      saveCourseSelections(currentIds);
    } else {
      if (selectedCourses.length > 0) {
        setSelectedCourses([]);
        setselectedCourseData([]);
        dispatch(setSelectedCourseData([]));
        clearCourseSelections();
      }
    }
  }, [activeCourses, teacher_id, searchCourse, courseActiveTab, periodTab, start_date, end_date, month_year, year, dispatch]);
  // ✅ activeCourses is stable via useMemo — this effect now only fires when
  //    courses truly change, not on every render.

  // Auto-select all courses for STUDENT when courses load or period changes
  useEffect(() => {
    if (
      student_id &&
      studentCoursesList.length > 0 && // ✅ stable memoized reference
      !searchCourse.trim() &&
      !isApiCallInProgressRef.current
    ) {
      const allCourseIds = studentCoursesList.map((c) => c.course_id);

      if (
        JSON.stringify([...allCourseIds].sort()) !==
        JSON.stringify([...selectedStudentCourses].sort())
      ) {
        setSelectedStudentCourses(allCourseIds);
        setselectstudent(allCourseIds);
        dispatch(setSelectedCourseData(allCourseIds));
        saveCourseSelections(allCourseIds);
      }
    }
  }, [studentCoursesList, student_id, periodTab, start_date, end_date, month_year, year]);
  //    fix - Was `studentcourse` (whole Redux slice — unstable).
  //    Now `studentCoursesList` (memoized array — only changes when courses data changes).

  const handleDeselectAllCourses = () => {
    if (isAIGenerating) return;
    hasUserInteractedRef.current = true;

    if (role === "Student") {
      setSelectedStudentCourses([]);
      setselectstudent([]);
      dispatch(setSelectedCourseData([]));
      saveCourseSelections([]);
    } else {
      setSelectedCourses([]);
      setselectedCourseData([]);
      dispatch(setSelectedCourseData([]));
      saveCourseSelections([]);
    }
  };

  const userRole = globalState?.teacher_details?.data?.teacher_department_roles;
  const hideTabs = ["advisor", "learning specialist", "hod"].includes(userRole);

  return (
    <>
      {globalState?.role === "Student" ? (
        <div
          className="courses-select-filter student"
          style={{
            opacity: isAIGenerating ? 0.5 : 1,
            pointerEvents: isAIGenerating ? "none" : "auto",
          }}
        >
          <div className="courses-select-filter-header">
            <h2>Courses</h2>
            <p>{selectedStudentCourses.length} Selected</p>
          </div>
          <div className="courses-select-filter-serach">
            <i className="fa-solid fa-magnifying-glass"></i>
            <input
              type="text"
              placeholder="Search for a course..."
              value={searchCourse}
              onChange={(e) => setsearchCourse(e.target.value)}
            />
          </div>
          <div className="courses-select-filter-results">
            {isStudentCoursesLoading ? (
              <CourseListLoader />
            ) : (
              <>
                <div className="Select-all">
                  <input
                    type="checkbox"
                    checked={
                      courseStudentData.length > 0 &&
                      courseStudentData.every((course) =>
                        selectedStudentCourses.includes(course.course_id),
                      )
                    }
                    onChange={() => {
                      hasUserInteractedRef.current = true;
                      const visibleCourseIds = courseStudentData.map(
                        (c) => c.course_id,
                      );
                      const allVisible = courseStudentData.every((c) =>
                        selectedStudentCourses.includes(c.course_id),
                      );
                      const newSelection = allVisible
                        ? selectedStudentCourses.filter(
                            (id) => !visibleCourseIds.includes(id),
                          )
                        : [
                            ...new Set([
                              ...selectedStudentCourses,
                              ...visibleCourseIds,
                            ]),
                          ];
                      setSelectedStudentCourses(newSelection);
                      setselectstudent(newSelection);
                      dispatch(setSelectedCourseData(newSelection));
                      saveCourseSelections(newSelection);
                    }}
                  />
                  <label
                    htmlFor="select-all"
                    onClick={() => {
                      hasUserInteractedRef.current = true;
                      const visibleCourseIds = courseStudentData.map(
                        (c) => c.course_id,
                      );
                      const allSelected =
                        selectedStudentCourses.length ===
                        courseStudentData.length;
                      const newSelection = allSelected
                        ? selectedStudentCourses.filter(
                            (id) => !visibleCourseIds.includes(id),
                          )
                        : [
                            ...new Set([
                              ...selectedStudentCourses,
                              ...visibleCourseIds,
                            ]),
                          ];
                      setSelectedStudentCourses(newSelection);
                      setselectstudent(newSelection);
                    }}
                    disabled={courseStudentData.length === 0}
                    style={
                      courseStudentData.length === 0
                        ? { cursor: "not-allowed", color: "#999" }
                        : { cursor: "pointer" }
                    }
                  >
                    Select All
                  </label>
                  <button
                    className="deselect-all-btn"
                    onClick={handleDeselectAllCourses}
                    disabled={courseStudentData.length === 0}
                    style={
                      courseStudentData.length === 0
                        ? { cursor: "not-allowed", color: "#999" }
                        : {}
                    }
                  >
                    Clear All
                  </button>
                </div>
                <div className="courses-select-filter-folder-s">
                  {courseStudentData.length > 0 ? (
                    courseStudentData.map((c) => (
                      <div className="course-item" key={c.course_id}>
                        <input
                          type="checkbox"
                          checked={selectedStudentCourses.includes(c.course_id)}
                          onChange={() => handleCheck(c.course_id)}
                        />
                        <label
                          htmlFor={`course${c.course_id}`}
                          onClick={() => handleCheck(c.course_id)}
                          style={{ cursor: "pointer" }}
                        >
                          {c.course_name.length > 25
                            ? c.course_name.slice(0, 25) + "..."
                            : c.course_name}{" "}
                        </label>
                      </div>
                    ))
                  ) : (
                    <p>No course selected yet</p>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      ) : (
        <div
          className="courses-select-filter teacher"
          style={{
            opacity: isAIGenerating ? 0.5 : 1,
            pointerEvents: isAIGenerating ? "none" : "auto",
          }}
        >
          <div className="courses-select-filter-header">
            <h2>Courses</h2>
            <p>{selectedCourses.length} Selected</p>
          </div>
          {hideTabs && (
            <div className="courses-select-tabs">
              <button
                className={courseActiveTab === "my_courses" ? "active" : ""}
                onClick={() => setCourseActiveTab("my_courses")}
              >
                My Courses
              </button>
              <button
                className={courseActiveTab === "other_courses" ? "active" : ""}
                onClick={() => setCourseActiveTab("other_courses")}
              >
                Other Courses
              </button>
            </div>
          )}
          <div className="courses-select-filter-serach">
            <i className="fa-solid fa-magnifying-glass"></i>
            <input
              type="text"
              placeholder="Search for a course..."
              value={searchCourse}
              onChange={(e) => setsearchCourse(e.target.value)}
            />
          </div>
          <div className="courses-select-filter-results">
            {isTeacherCoursesLoading ? (
              <CourseListLoader />
            ) : courseData.length > 0 ? (
              <>
                <div className="Select-all">
                  <input
                    type="checkbox"
                    checked={
                      courseData.length > 0 &&
                      courseData.every((course) =>
                        selectedCourses.includes(course.course_id),
                      )
                    }
                    onChange={() => {
                      hasUserInteractedRef.current = true;
                      const visibleCourseIds = courseData.map(
                        (c) => c.course_id,
                      );
                      const allVisible = courseData.every((c) =>
                        selectedCourses.includes(c.course_id),
                      );
                      const newSelection = allVisible
                        ? selectedCourses.filter(
                            (id) => !visibleCourseIds.includes(id),
                          )
                        : [
                            ...new Set([
                              ...selectedCourses,
                              ...visibleCourseIds,
                            ]),
                          ];
                      setSelectedCourses(newSelection);
                      setselectedCourseData(newSelection);
                      dispatch(setSelectedCourseData(newSelection));
                      saveCourseSelections(newSelection);
                    }}
                  />
                  <label
                    htmlFor="select-all"
                    onClick={() => {
                      hasUserInteractedRef.current = true;
                      const visibleCourseIds = courseData.map(
                        (c) => c.course_id,
                      );
                      const allSelected =
                        selectedCourses.length === courseData.length;
                      const newSelection = allSelected
                        ? selectedCourses.filter(
                            (id) => !visibleCourseIds.includes(id),
                          )
                        : [
                            ...new Set([
                              ...selectedCourses,
                              ...visibleCourseIds,
                            ]),
                          ];
                      setSelectedCourses(newSelection);
                      setselectedCourseData(newSelection);
                    }}
                    disabled={courseData.length === 0}
                    style={
                      courseData.length === 0
                        ? { cursor: "not-allowed", color: "#999" }
                        : { cursor: "pointer" }
                    }
                  >
                    Select All
                  </label>
                  <button
                    className="deselect-all-btn"
                    onClick={handleDeselectAllCourses}
                    disabled={courseData.length === 0}
                    style={
                      courseData.length === 0
                        ? { cursor: "not-allowed", color: "#999" }
                        : {}
                    }
                  >
                    Clear All
                  </button>
                </div>
                <div className="courses-select-filter-folder">
                  {courseData.map((c) => (
                    <div
                      className="course-item flex items-center justify-between gap-2"
                      key={c.course_id}
                    >
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <input
                          type="checkbox"
                          checked={selectedCourses.includes(c.course_id)}
                          onChange={() => handleCheck(c.course_id)}
                        />

                        <label
                          htmlFor={`course${c.course_id}`}
                          onClick={() => handleCheck(c.course_id)}
                          style={{ cursor: "pointer" }}
                          className="truncate flex-1"
                        >
                          {c.course_name.length > 25
                            ? c.course_name.slice(0, 25) + "..."
                            : c.course_name}
                        </label>
                      </div>

                      {c.is_archived && (
                        <img
                          className="h-5 w-5 flex-shrink-0 cursor-default"
                          src={unarchive_btn}
                          alt=""
                          data-tooltip-id="archived-course"

                        />
                      )}
                    </div>
                  ))}

                  <Tooltip
                    id="archived-course"
                    place="top"
                    content="Archived Course"
                  />
                </div>
              </>
            ) : (
              <div className="error-message-overviewperformence-dashboard">
                <div className="error-message-overviewperformence-dashboard-image">
                  <img src={NoCoursesFoundImage} alt="No courses found" />
                </div>
                <p>No courses available yet</p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default CoursesSelectFilter;
